import {useEffect, useMemo, useState} from 'react'
import {useLayout} from '../../../_metronic/layout/core'
import {
  createExpense,
  getCostCenterOptions,
  getProfitCenterOptions,
  CostCenterOption,
  ProfitCenterOption,
} from './api'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import toast from 'react-hot-toast'
import Select from 'react-select'
import {removeIndicatorSeparator, solidSelectStyles} from '../shared/ui/selectStyles'

// ─── Validation Schema ────────────────────────────────────────────────────────

const expenseSchema = Yup.object().shape({
  expense_title: Yup.string().required('Expense title is required'),
  category: Yup.string().required('Category is required'),
  cost_center_id: Yup.number()
    .min(1, 'Cost centre is required')
    .required('Cost centre is required'),
  profit_center_id: Yup.number(),
  priority: Yup.string().required('Priority is required'),
  amount: Yup.number().min(0.01, 'Amount must be greater than 0').required('Amount is required'),
  currency: Yup.string().required('Currency is required'),
  expense_date: Yup.string().required('Expense date is required'),
  vendor_name: Yup.string(),
  reference_no: Yup.string(),
  requested_by: Yup.string().required('Requested by is required'),
  description: Yup.string().required('Description is required'),
})

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORY_OPTIONS = [
  {value: 'travel', label: 'Travel'},
  {value: 'meals', label: 'Meals'},
  {value: 'supplies', label: 'Supplies'},
  {value: 'equipment', label: 'Equipment'},
  {value: 'utilities', label: 'Utilities'},
  {value: 'professional_services', label: 'Professional Services'},
  {value: 'salaries', label: 'Salaries'},
  {value: 'maintenance', label: 'Maintenance'},
  {value: 'marketing', label: 'Marketing'},
  {value: 'training', label: 'Training'},
  {value: 'entertainment', label: 'Entertainment'},
  {value: 'rent', label: 'Rent'},
  {value: 'insurance', label: 'Insurance'},
  {value: 'taxes', label: 'Taxes'},
  {value: 'others', label: 'Others'},
]

const PRIORITY_OPTIONS = [
  {value: 'low', label: 'LOW'},
  {value: 'medium', label: 'MEDIUM'},
  {value: 'high', label: 'HIGH'},
  {value: 'urgent', label: 'URGENT'},
]

const CURRENCY_OPTIONS = [
  {value: 'INR', label: 'INR'},
  {value: 'USD', label: 'USD'},
  {value: 'EUR', label: 'EUR'},
]

const getTodayDate = () => new Date().toISOString().split('T')[0]

// ─── Types ────────────────────────────────────────────────────────────────────

interface ExpenseFormValues {
  expense_title: string
  category: string
  cost_center_id: number
  profit_center_id: number
  priority: string
  amount: number
  currency: string
  expense_date: string
  vendor_name: string
  reference_no: string
  requested_by: string
  description: string
}

type OptionType = {
  value: string | number
  label: string
}

// ─── Component ────────────────────────────────────────────────────────────────

export const CreateExpense: React.FC<{
  setCreateExpense: React.Dispatch<React.SetStateAction<boolean>>
  setShouldRefreshExpenses: React.Dispatch<React.SetStateAction<boolean>>
}> = ({setCreateExpense, setShouldRefreshExpenses}) => {
  const {company} = useLayout()
  const [loading, setLoading] = useState(false)

  const [costCenterOptions, setCostCenterOptions] = useState<OptionType[]>([])
  const [profitCenterOptions, setProfitCenterOptions] = useState<OptionType[]>([])

  const [category, setCategory] = useState<OptionType>({label: 'Select Category...', value: ''})
  const [costCenter, setCostCenter] = useState<OptionType>({
    label: 'Select Cost Centre...',
    value: 0,
  })
  const [profitCenter, setProfitCenter] = useState<OptionType>({
    label: 'Select Profit Centre...',
    value: 0,
  })
  const [priority, setPriority] = useState<OptionType>({label: 'Select Priority...', value: ''})
  const [currency, setCurrency] = useState<OptionType>({label: 'INR', value: 'INR'})

  const initialValues: ExpenseFormValues = {
    expense_title: '',
    category: '',
    cost_center_id: 0,
    profit_center_id: 0,
    priority: '',
    amount: 0,
    currency: 'INR',
    expense_date: getTodayDate(),
    vendor_name: '',
    reference_no: '',
    requested_by: '',
    description: '',
  }

  const formik = useFormik<ExpenseFormValues>({
    initialValues,
    validationSchema: expenseSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      setLoading(true)
      const body = {
        expense_id: null,
        company_id: company?.value,
        expense_title: values.expense_title,
        category: values.category,
        cost_center_id: values.cost_center_id,
        profit_center_id: values.profit_center_id || 0,
        priority: values.priority,
        amount: values.amount,
        currency: values.currency,
        expense_date: values.expense_date,
        vendor_name: values.vendor_name || '',
        reference_no: values.reference_no || '',
        requested_by: values.requested_by,
        description: values.description,
      }
      await createExpense(body)
        .then((res) => {
          res?.detail && toast.success('Expense has been recorded successfully.')
          formik.resetForm()
          setShouldRefreshExpenses(true)
        })
        .catch((err) => toast.error(err?.detail || err.message))
        .finally(() => {
          setLoading(false)
          setCreateExpense(false)
        })
    },
  })

  // Load cost center and profit center options
  useMemo(async () => {
    if (company?.value) {
      const [ccRes, pcRes] = await Promise.all([
        getCostCenterOptions(company.value),
        getProfitCenterOptions(company.value),
      ])
      if (ccRes) {
        setCostCenterOptions(
          ccRes.map((c: CostCenterOption) => ({label: c.center_name, value: c.cost_center_id}))
        )
      }
      if (pcRes) {
        setProfitCenterOptions(
          pcRes.map((p: ProfitCenterOption) => ({label: p.center_name, value: p.profit_center_id}))
        )
      }
    }
  }, [company?.value])

  // Sync selects back into formik
  useEffect(() => {
    formik.setFieldValue('category', category.value, true)
    formik.setFieldValue('cost_center_id', costCenter.value, true)
    formik.setFieldValue('profit_center_id', profitCenter.value, true)
    formik.setFieldValue('priority', priority.value, true)
    formik.setFieldValue('currency', currency.value, true)
  }, [category.value, costCenter.value, profitCenter.value, priority.value, currency.value])

  return (
    <div className='card'>
      <div
        className='card-header border-0'
        data-bs-target='#kt_expense_form'
        aria-expanded='true'
        aria-controls='kt_expense_form'
      >
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>Add New Expense</h3>
        </div>
      </div>

      <form onSubmit={formik.handleSubmit} noValidate className='form'>
        <div
          id='kt_expense_form'
          className='collapse show d-flex justify-content-center flex-column flex-md-row'
        >
          {/* Left Column */}
          <div className='p-8 col pb-0 pb-md-4'>
            {/* Expense Title */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Expense Title</label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Expense Title'
                  {...formik.getFieldProps('expense_title')}
                />
                {formik.touched.expense_title && formik.errors.expense_title && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.expense_title}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Category */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Category</label>
              <div className='col-lg-9 fv-row'>
                <Select
                  value={category}
                  onChange={(option: any) => setCategory(option as OptionType)}
                  options={CATEGORY_OPTIONS}
                  isSearchable={true}
                  className='fw-bold'
                  styles={solidSelectStyles}
                  components={removeIndicatorSeparator}
                />
              </div>
            </div>

            {/* Cost Centre */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Cost Centre</label>
              <div className='col-lg-9 fv-row'>
                <Select
                  value={costCenter}
                  onChange={(option: any) => setCostCenter(option as OptionType)}
                  options={costCenterOptions}
                  isSearchable={true}
                  className='fw-bold'
                  styles={solidSelectStyles}
                  components={removeIndicatorSeparator}
                />
              </div>
            </div>

            {/* Profit Centre */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label fw-bold fs-6'>Profit Centre</label>
              <div className='col-lg-9 fv-row'>
                <Select
                  value={profitCenter}
                  onChange={(option: any) => setProfitCenter(option as OptionType)}
                  options={profitCenterOptions}
                  isSearchable={true}
                  className='fw-bold'
                  styles={solidSelectStyles}
                  components={removeIndicatorSeparator}
                />
              </div>
            </div>

            {/* Priority */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Priority</label>
              <div className='col-lg-9 fv-row'>
                <Select
                  value={priority}
                  onChange={(option: any) => setPriority(option as OptionType)}
                  options={PRIORITY_OPTIONS}
                  isSearchable={false}
                  className='fw-bold'
                  styles={solidSelectStyles}
                  components={removeIndicatorSeparator}
                />
              </div>
            </div>

            {/* Expense Date */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Expense Date</label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='date'
                  className='form-control form-control-lg form-control-solid'
                  {...formik.getFieldProps('expense_date')}
                />
                {formik.touched.expense_date && formik.errors.expense_date && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.expense_date}</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className='p-8 col pt-0 pt-md-8 pb-md-4'>
            {/* Amount */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Amount</label>
              <div className='col-lg-6 fv-row'>
                <input
                  type='number'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Amount'
                  {...formik.getFieldProps('amount')}
                />
                {formik.touched.amount && formik.errors.amount && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.amount}</div>
                  </div>
                )}
              </div>
              <div className='col-lg-3 fv-row'>
                <Select
                  value={currency}
                  onChange={(option: any) => setCurrency(option as OptionType)}
                  options={CURRENCY_OPTIONS}
                  isSearchable={false}
                  className='fw-bold'
                  styles={solidSelectStyles}
                  components={removeIndicatorSeparator}
                />
              </div>
            </div>

            {/* Vendor */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label fw-bold fs-6'>Vendor / Supplier</label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Vendor / Supplier'
                  {...formik.getFieldProps('vendor_name')}
                />
              </div>
            </div>

            {/* Reference */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label fw-bold fs-6'>Reference / Invoice</label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Reference / Invoice No'
                  {...formik.getFieldProps('reference_no')}
                />
              </div>
            </div>

            {/* Requested By */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Requested By</label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Requested By'
                  {...formik.getFieldProps('requested_by')}
                />
                {formik.touched.requested_by && formik.errors.requested_by && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.requested_by}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Description</label>
              <div className='col-lg-9 fv-row'>
                <textarea
                  className='form-control form-control-lg form-control-solid'
                  placeholder='Description'
                  rows={4}
                  {...formik.getFieldProps('description')}
                />
                {formik.touched.description && formik.errors.description && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.description}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className='card-footer d-flex justify-content-end px-9 border-0 pb-7 pt-0'>
          <button
            type='button'
            className='btn btn-secondary'
            style={{marginRight: '10px'}}
            onClick={() => {
              formik.resetForm()
              setCreateExpense(false)
              setShouldRefreshExpenses(false)
            }}
          >
            Cancel
          </button>
          <button
            type='submit'
            className='btn btn-primary ml-2'
            disabled={loading || !formik.isValid}
          >
            {!loading ? 'Save Expense' : null}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Please wait...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}
